import { Injectable, Optional } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Utilities } from '@app/common/utilities';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { CacheService } from '@app/services/cache.service';
import { InternetStatusService } from '@app/services/internet-status.service';
import { Observable, of, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HttpHelper {

  constructor(@Optional() private snackBar: MatSnackBar, private cache: CacheService, private internetStatusService: InternetStatusService) { }

  public handleError<T>({ operation, result, rethrow = false, cacheKey, showErrorSnackbar = true }: { operation: string; result?: T; rethrow?: boolean; cacheKey?: string, showErrorSnackbar?: boolean }): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {

      // log error
      console.error(operation, error);

      if (error.status === 402) {
        // We return 402 Payment Required when we're not licensed to access a sharepoint api - https://tools.ietf.org/html/rfc7231#section-6.5.2
        // The error is actually that we're not licensed to call the api so we just return whatever default has been specified.
        console.log('The user is unlicensed to call the requested API');
        return of(result as T);
      }

      if (error.status === 429) {
        // There are too many people trying to do the same thing as you, wait a minute and try again
        console.log('There are too many requests to the resource, wait a minute and try again');
        return of(result as T);
      }

      const errorInfo = error.status + ';' + operation;
      if (error?.status && showErrorSnackbar) {
        this.snackBar && Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, errorInfo, 5000, error);
      }

      // Handle if we're offline then return cached data.
      if (cacheKey &&
        (
          error?.status === 504 // Service worker reports 504 when we're offline
          ||
          (error?.message?.indexOf('Http failure response for') > -1 && error?.status === 0) // this case usually happens if DNS fails for a service or ethernetcable is unplugged
          ||
          this.internetStatusService.state.online === false // We know that we're offline due to browser events
        )
      ) {
        const cachedValue = this.cache.get<T>(cacheKey);
        if (cachedValue) {
          this.snackBar && Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.offline, 5000);
          return of(cachedValue);
        }
      }

      if (rethrow) {
        return throwError(error);
      }

      // Let the app keep running by returning an empty or cached result.
      return of(result as T);
    };
  }

}
